import * as React from "react"
import { ChatCircleDots, X } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"

/**
 * Compagnon flottant naia — pattern officiel (Claude Design, previews/chatbot.html) :
 * bulle discrète pendant le travail, qui s'ouvre en panneau par-dessus le contenu,
 * jamais un écran séparé. Coquille de présentation uniquement — messages et saisie
 * sont fournis par l'appelant (`children` / `footer`) ; ce composant ne parle à
 * aucune API de chat.
 */

interface FloatingChatProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  children: React.ReactNode
  footer: React.ReactNode
  className?: string
}

const BUBBLE_SIZE = 56
const BUBBLE_MARGIN = 20
const DRAG_THRESHOLD = 8
const POSITION_STORAGE_KEY = "naia-floating-chat-bubble-position"

interface BubblePosition {
  x: number
  y: number
}

type BubbleSide = "left" | "right"

interface StoredBubblePosition {
  side: BubbleSide
  y: number
}

/** Ramène la position dans les limites visibles de l'écran (resize/rotation compris). */
function clampPosition(position: BubblePosition): BubblePosition {
  const maxX = window.innerWidth - BUBBLE_SIZE - BUBBLE_MARGIN
  const maxY = window.innerHeight - BUBBLE_SIZE - BUBBLE_MARGIN
  return {
    x: Math.min(Math.max(position.x, BUBBLE_MARGIN), Math.max(maxX, BUBBLE_MARGIN)),
    y: Math.min(Math.max(position.y, BUBBLE_MARGIN), Math.max(maxY, BUBBLE_MARGIN)),
  }
}

function clampY(y: number): number {
  const maxY = window.innerHeight - BUBBLE_SIZE - BUBBLE_MARGIN
  return Math.min(Math.max(y, BUBBLE_MARGIN), Math.max(maxY, BUBBLE_MARGIN))
}

/** Bord le plus proche du centre de la bulle (jamais au milieu de l'écran). */
function sideFromX(x: number): BubbleSide {
  const center = x + BUBBLE_SIZE / 2
  return center < window.innerWidth / 2 ? "left" : "right"
}

function positionForSide(side: BubbleSide, y: number): BubblePosition {
  const x = side === "left" ? BUBBLE_MARGIN : window.innerWidth - BUBBLE_SIZE - BUBBLE_MARGIN
  return { x, y: clampY(y) }
}

function readStoredPosition(): StoredBubblePosition | null {
  try {
    const raw = window.localStorage.getItem(POSITION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredBubblePosition>
    if ((parsed.side !== "left" && parsed.side !== "right") || typeof parsed.y !== "number") {
      return null
    }
    return { side: parsed.side, y: parsed.y }
  } catch {
    return null
  }
}

function writeStoredPosition(position: StoredBubblePosition): void {
  try {
    window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position))
  } catch {
    /* localStorage indisponible (navigation privée, quota) : la session reste fonctionnelle. */
  }
}

function isCoarsePointer(): boolean {
  return window.matchMedia("(pointer: coarse)").matches
}

function FloatingChatBubble({
  onOpenChange,
  className,
}: {
  onOpenChange: (open: boolean) => void
  className?: string
}) {
  const [position, setPosition] = React.useState<BubblePosition | null>(null)
  const side = React.useRef<BubbleSide | null>(null)
  const dragState = React.useRef<{
    pointerId: number
    startX: number
    startY: number
    origin: BubblePosition
    dragging: boolean
  } | null>(null)

  React.useLayoutEffect(() => {
    if (!isCoarsePointer()) return
    const stored = readStoredPosition()
    if (!stored) return
    side.current = stored.side
    setPosition(positionForSide(stored.side, stored.y))
  }, [])

  React.useEffect(() => {
    if (!isCoarsePointer()) return
    const handleResize = () => {
      setPosition((current) => {
        if (!current) return current
        if (side.current) return positionForSide(side.current, current.y)
        return clampPosition(current)
      })
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!isCoarsePointer()) return
    const rect = event.currentTarget.getBoundingClientRect()
    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: { x: rect.left, y: rect.top },
      dragging: false,
    }
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    if (!drag.dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
    if (!drag.dragging) {
      drag.dragging = true
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    const next = clampPosition({ x: drag.origin.x + dx, y: drag.origin.y + dy })
    setPosition(next)
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== event.pointerId) return
    if (drag.dragging) {
      event.currentTarget.releasePointerCapture(event.pointerId)
      setPosition((current) => {
        if (!current) return current
        const snappedSide = sideFromX(current.x)
        const snapped = positionForSide(snappedSide, current.y)
        side.current = snappedSide
        writeStoredPosition({ side: snappedSide, y: snapped.y })
        return snapped
      })
    } else {
      onOpenChange(true)
    }
    dragState.current = null
  }

  const handlePointerCancel = () => {
    dragState.current = null
  }

  return (
    <button
      type="button"
      data-slot="floating-chat-bubble"
      aria-label="Ouvrir naia"
      onClick={() => {
        if (!isCoarsePointer()) onOpenChange(true)
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      style={
        position
          ? { position: "fixed", left: position.x, top: position.y, right: "auto", bottom: "auto" }
          : undefined
      }
      className={cn(
        "fixed right-5 bottom-5 z-40 inline-flex size-14 touch-none items-center justify-center rounded-full bg-primary text-primary-foreground shadow-hero transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        className
      )}
    >
      <ChatCircleDots size={26} weight="fill" />
    </button>
  )
}

function FloatingChatPanel({
  onOpenChange,
  title = "naia",
  children,
  footer,
  className,
}: Omit<FloatingChatProps, "open">) {
  return (
    <div
      data-slot="floating-chat-panel"
      role="dialog"
      aria-label={title}
      className={cn(
        "fixed inset-x-4 bottom-4 z-40 flex max-h-[min(640px,calc(100vh-2rem))] flex-col overflow-hidden rounded-card border border-border bg-card shadow-hero sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[380px]",
        className
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-[13px] font-bold text-primary-foreground">
          n
        </span>
        <span className="text-[13px] font-bold text-foreground">{title}</span>
        <button
          type="button"
          aria-label="Fermer"
          onClick={() => onOpenChange(false)}
          className="ml-auto inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted pointer-coarse:size-11"
        >
          <X size={18} weight="bold" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>
      <div className="shrink-0 border-t border-border px-3 py-2.5">{footer}</div>
    </div>
  )
}

function FloatingChat({ open, onOpenChange, ...rest }: FloatingChatProps) {
  return open ? (
    <FloatingChatPanel onOpenChange={onOpenChange} {...rest} />
  ) : (
    <FloatingChatBubble onOpenChange={onOpenChange} />
  )
}

export { FloatingChat, FloatingChatBubble, FloatingChatPanel }
